<!-- 
Dashboard widget showing total levies broken down by levy component (admin, risk rated, special tuition protection).

This widget has two modes, one showing a doughnut chart and the other showing a bar chart.
-->
<template>
  <v-flex xs12 md6 lg4 xl3>
    <v-card flat class="home">
      <our-widget-toolbar 
        :fixed="fixed" 
        title="Levy components" 
        @refresh="forceReload" 
        @remove="$emit('removeWidget')" 
        @toggleChartType="$emit('toggle')"
      />
      <our-busy-indicator :show="!loaded"/>
      <v-card-text class="pb-3">
        <doughnut-chart 
          v-if="toggled && !isChartEmpty(chartData)" ref="doughnutChart" :chart-data="chartData" :chart-options="doughnutOptions" 
          style="height: 265px"
        />
        <bar-chart 
          v-if="!toggled && !isChartEmpty(chartData)" ref="barChart" :chart-data="chartData" :chart-options="barOptions" 
          style="height: 265px"
        />
        <our-chart-is-empty-placeholder v-if="isChartEmpty(chartData)" style="height: 265px" />
      </v-card-text>
    </v-card>
  </v-flex>
</template>

<script>
import DoughnutChart from "@/components/common/chart/DoughnutChart.vue";
import BarChart from "@/components/common/chart/BarChart.vue";
import _ from "underscore";
import * as formatting from "@/helpers/formatting.helper";
import * as dashboardAction from "@/store/dashboard/action-types";
import * as helpers from "@/store/levy-collections/helpers";
import chartMixins from "@/mixins/chart.mixins";

export default {
  components: {
    DoughnutChart,
    BarChart
  },
  mixins: [chartMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    toggled: {
      type: Boolean,
      default: false
    },
    fixed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      doughnutOptions: {
        responsive: true,
        maintainAspectRatio: false,
        onResize: this.resized,
        legend: { display: true, position: "bottom" },
        tooltips: {
          callbacks: { label: this.showLabel }
        }
      },
      barOptions: {
        responsive: true,
        maintainAspectRatio: false,
        onResize: this.resized,
        tooltips: {
          callbacks: { label: this.showLabel }
        },
        scales: {
          yAxes: [{ ticks: { min: 0, callback: label => formatting.currency0(label) } }]
        }
      }
    };
  },
  computed: {
    state() {
      return this.$store.state.dashboard.levyComponents;
    },
    loading() {
      return this.state.loading;
    },
    loaded() {
      return this.state.loaded;
    },
    chartData() {
      return {
        labels: _.map(this.state.data, d => {
          return d.componentName;
        }),
        datasets: [
          {
            data: _.map(this.state.data, d => {
              return d.total;
            }),
            backgroundColor: helpers.ComponentChartColors
          }
        ]
      };
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    },
    toggled() {
      this.drawChart();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    forceReload() {
      this.$store.dispatch(dashboardAction.LEVY_COMPONENTS_GET, this.levyCollectionId);
    },
    refresh() {
      this.$store.dispatch(dashboardAction.LEVY_COMPONENTS_REFRESH, this.levyCollectionId);
      if (this.loaded) {
        this.drawChart();
      }
    },
    drawChart: function() {
      this.$nextTick(() => {
        if (this.toggled) {
          if (this.$refs.doughnutChart) {
            this.$refs.doughnutChart.display();
          }
        } else {
          if (this.$refs.barChart) {
            this.$refs.barChart.display();
          }
        }
      });
    },
    showLabel: function(tooltipItem, data) {
      const index = tooltipItem.index;
      const number = formatting.currency(data.datasets[0].data[index]);
      var label = `${this.toggled ? data.labels[index] + ": " : ""}${number}`;
      return label;
    },
    removeWidget() {
      this.$emit("removeWidget");
    },
    resized(chart, size) {
      var legendPosition = chart.options.legend.position;
      var newPosition = size.width < 450 ? "bottom" : "right";
      if (newPosition != legendPosition) {
        chart.options.legend.position = newPosition;
        chart.update();
      }
    }
  }
};
</script>
